import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { TokenCookieService } from './token-storage-cookies.service';

interface IdleTimerConfig {
  timeout: number; // in seconds
  onTimeout: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class IdleTimer {
  private timeout: number;
  private onTimeout: Function;
  private timerId: any;
  private isRunning = false;

  private eventHandler = (event: Event) => {
    // console.log(`[IdleTimer] Activity detected: ${event.type} at ${new Date().toLocaleTimeString()}`);
    this.reset();
  };

  constructor(
    private snackBar: MatSnackBar,
    private tokenCookieService: TokenCookieService,
    private ngZone: NgZone
  ) {}

  start(config: IdleTimerConfig): void {
    const currentUser = this.tokenCookieService.getUser();
    // console.log('[IdleTimer] Start called with user:', currentUser);

    if (!currentUser || !currentUser?.refreshToken || currentUser?.refreshToken === null ) {
      console.warn('[IdleTimer] No logged-in user found. Timer not started.');
      return;
    }

    this.timeout = config.timeout;
    this.onTimeout = config.onTimeout;
    this.attachActivityListeners();
    this.isRunning = true;
    this.reset();
    // console.log(`[IdleTimer] Started with timeout = ${this.timeout} seconds.`);
  }

  reset(): void {
    if (!this.isRunning) {
      // console.log('[IdleTimer] Reset called but timer is not running.');
      return;
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      // console.log('[IdleTimer] Cleared timer ID:', this.timerId);
    }

    const expiryTime = new Date(Date.now() + this.timeout * 1000);
    // console.log(`[IdleTimer] Timer reset - next expiry at ${expiryTime.toLocaleTimeString()}`);

    this.timerId = setTimeout(() => {
      console.warn('[IdleTimer] Timeout reached - triggering logout.');
      this.ngZone.run(() => {
        this.showSnackBar('You have been logged out due to inactivity.');
        this.onTimeout?.();
        this.stop();
      });
    }, this.timeout * 1000);
    // console.log('[IdleTimer] New timer set with ID:', this.timerId);
  }

  stop(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      // console.log('[IdleTimer] Timer stopped with ID:', this.timerId);
    }
    this.detachActivityListeners();
    this.isRunning = false;
    // console.log('[IdleTimer] Stopped.');
  }

  private attachActivityListeners(): void {
    const events = ['mousemove', 'scroll', 'keydown', 'click'];
    events.forEach(e => window.addEventListener(e, this.eventHandler, { passive: true }));
    // console.log('[IdleTimer] Activity listeners attached for:', events);
  }

  private detachActivityListeners(): void {
    const events = ['mousemove', 'scroll', 'keydown', 'click'];
    events.forEach(e => window.removeEventListener(e, this.eventHandler));
    // console.log('[IdleTimer] Activity listeners detached.');
  }

  private showSnackBar(message: string): void {
    const horizontalPosition: MatSnackBarHorizontalPosition = 'end';
    const verticalPosition: MatSnackBarVerticalPosition = 'top';
    const duration = 5000;
    const panelClass = ['snackbar-danger', 'login-snackbar'];

    // console.log('[IdleTimer] Displaying snackbar:', message);
    this.snackBar.open(message, 'X', {
      horizontalPosition,
      verticalPosition,
      duration,
      panelClass,
    });
  }
}